import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scissors, MessagesSquare, CircleDollarSign, Store, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { storeApi, formatStoreError } from "@/lib/storefront";
import ProductCard from "@/components/storefront/ProductCard";
import SEO from "@/components/storefront/SEO";
import { BRAND } from "@/lib/brand";
import useStoreRefresh from "@/hooks/useStoreRefresh";

const HERO_IMG = "https://images.unsplash.com/photo-1626784579980-db39c1a13aa9?w=2400&auto=format&fit=crop&q=80";
const LIFESTYLE_IMG = "https://images.unsplash.com/photo-1594526761005-4ccdbd608d2b?w=1600&auto=format&fit=crop&q=80";
const B2B_IMG = "https://images.unsplash.com/photo-1771098206750-6be5aef4f503?w=1600&auto=format&fit=crop&q=80";

const BENEFITS = [
  [Scissors, "Curated by makers", "Every ruler, thread, and fat quarter is hand-picked by people who actually quilt and sew — no filler listings, ever."],
  [MessagesSquare, "Maker-first support", "Email us and a fellow maker replies within one business day. Real advice, not canned scripts."],
  [CircleDollarSign, "Fair, honest prices", "Everyday staples at prices that respect your craft budget — and free US shipping over $" + 75 + "."],
  [Store, "Wholesale-friendly", "Quilt shops, sewing studios, and longarm quilters get small-batch wholesale with fair minimums."],
];

export default function Home() {
  const [data, setData] = useState(null);
  const [tree, setTree] = useState([]);
  const [nlEmail, setNlEmail] = useState("");
  const [nlSending, setNlSending] = useState(false);

  useStoreRefresh(() => {
    storeApi.get("/home")
      .then((r) => setData(r.data))
      .catch((err) => console.error("Home load:", err));
    storeApi.get("/categories/tree")
      .then((r) => setTree(r.data || []))
      .catch((err) => console.error("Tree load:", err));
  }, []);

  const shopCategories = tree.filter((c) => c.slug !== "gift-cards").slice(0, 6);
  const comingSoon = (data?.featured || []).filter((p) => p.coming_soon);
  const arrivals = comingSoon.length > 0 ? comingSoon : (data?.featured || []);

  const notifySubscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail || !nlEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setNlSending(true);
    try {
      const { data: res } = await storeApi.post("/newsletter", { email: nlEmail });
      toast.success(res.message || "You're on the list");
      setNlEmail("");
    } catch (err) {
      toast.error(formatStoreError(err));
    } finally {
      setNlSending(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description={`${BRAND.name} — a US-based online shop for quilting, sewing, patchwork, and craft supplies. Fabric, rulers, cutting mats, threads, notions, and patterns — curated by makers, for makers. Free US shipping over $${BRAND.freeShippingThreshold}.`}
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="A maker sewing at a sunlit table" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2B2127]/70 via-[#2B2127]/35 to-transparent" />
        </div>
        <div className="relative mx-auto flex min-h-[480px] max-w-[1440px] flex-col justify-center px-6 py-20 md:min-h-[600px] md:px-10 md:py-28">
          <div className="max-w-xl">
            <div className="mb-4 text-[11px] uppercase tracking-[0.3em] text-white/80 md:text-xs">Quilting · Sewing · Patchwork</div>
            <h1 className="font-display text-[38px] leading-[1.05] text-white sm:text-5xl md:text-[64px]" data-testid="hero-headline">
              Everything your next <span className="font-display-soft italic">quilt</span> needs
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/90 md:text-lg">
              Fabric, rulers, rotary cutters, and threads — hand-picked staples for American quilters and sewists, from first fat quarter to finished binding.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/catalog" className="inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#59253d]" data-testid="hero-cta-primary">
                Shop the collection <ArrowRight size={15} />
              </Link>
              <Link to="/wholesale" className="inline-flex items-center gap-2 rounded-full border border-white/70 px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-white hover:text-[#2B2127]" data-testid="hero-cta-secondary">
                Wholesale for studios
              </Link>
            </div>
            <div className="mt-6 text-[12px] uppercase tracking-[0.18em] text-white/75">
              US-based support · Maker-first promise · Free shipping over ${BRAND.freeShippingThreshold}
            </div>
          </div>
        </div>
      </section>

      {/* WHY MAKERS CHOOSE US */}
      <section className="border-y border-[#E6D4CC] bg-white py-12 md:py-16" data-testid="benefits-strip">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="mb-8 md:mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B5158] md:text-xs">The promise</div>
            <h2 className="mt-2 font-display text-[26px] leading-tight text-[#2B2127] md:text-4xl">
              Why makers choose <span className="font-display-soft italic">{BRAND.name}</span>
            </h2>
          </div>
          <div className="grid gap-8 md:grid-cols-4 md:gap-10">
            {BENEFITS.map(([Icon, title, body], i) => (
              <div key={title} className={`flex items-start gap-4 store-fade-up store-fade-up-d${Math.min(i, 4)}`}>
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#F1E3DC] text-[#9D5C6C]">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-display text-xl leading-tight text-[#2B2127]">{title}</div>
                  <div className="mt-1.5 text-[15px] leading-relaxed text-[#3A2E33]">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      {shopCategories.length > 0 && (
        <section className="border-b border-[#E6D4CC] bg-[#FAF6F1] py-12 md:py-20" data-testid="browse-by-category">
          <div className="mx-auto max-w-[1440px] px-5 md:px-10">
            <div className="mb-6 flex items-end justify-between gap-4 md:mb-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B5158] md:text-xs">Popular categories</div>
                <h2 className="mt-2 font-display text-[28px] leading-tight text-[#2B2127] md:text-5xl">
                  Shop by <span className="font-display-soft italic">category</span>
                </h2>
              </div>
              <Link to="/catalog" className="hidden items-center gap-1 text-sm text-[#9D5C6C] hover:underline md:inline-flex">All categories <ArrowRight size={14} /></Link>
            </div>

            {/* MOBILE — 2-col photo tiles */}
            <div className="grid grid-cols-2 gap-3 md:hidden">
              {shopCategories.map((c) => (
                <Link key={c.id} to={`/c/${c.slug}`} className="group relative block aspect-[3/4] overflow-hidden rounded-md bg-[#F1E3DC]" data-testid={`browse-tile-${c.slug}`}>
                  {c.image && (
                    <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-3">
                    <div className="font-display text-[16px] uppercase leading-tight tracking-[0.04em] text-white drop-shadow">{c.name}</div>
                  </div>
                  <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#2B2127] shadow-[0_4px_14px_-4px_rgba(0,0,0,0.4)]">
                    <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>

            {/* DESKTOP — 3 columns: image + name + description + subcategory list */}
            <div className="hidden gap-x-6 gap-y-12 md:grid md:grid-cols-3">
              {shopCategories.map((c) => (
                <div key={c.id} className="space-y-4" data-testid={`browse-col-${c.slug}`}>
                  <Link to={`/c/${c.slug}`} className="group block">
                    {c.image ? (
                      <div className="aspect-[4/3] overflow-hidden rounded-md bg-white">
                        <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" loading="lazy" />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] rounded-md bg-[#F1E3DC]" />
                    )}
                    <div className="mt-3 font-display text-[20px] font-medium uppercase tracking-[0.14em] text-[#2B2127] group-hover:text-[#9D5C6C]">
                      {c.name}
                    </div>
                  </Link>
                  {c.description && <p className="text-[14px] leading-relaxed text-[#6B5158]">{c.description}</p>}
                  {c.children?.length > 0 && (
                    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-[14px] text-[#3A2E33]">
                      {c.children.slice(0, 5).map((sub) => (
                        <li key={sub.id}>
                          <Link to={`/c/${sub.slug}`} className="hover:text-[#9D5C6C] hover:underline" data-testid={`browse-sub-${sub.slug}`}>
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SHOP BY PROJECT + MAKER RESOURCES */}
      <section className="border-b border-[#E6D4CC] bg-white py-14 md:py-20" data-testid="shop-by-project">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B5158] md:text-xs">Start with the project</div>
              <h2 className="mt-2 font-display text-[26px] leading-tight text-[#2B2127] md:text-4xl">
                Shop by <span className="font-display-soft italic">project</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[
              ["Baby quilt", "Soft cottons & easy precuts", "/c/fabric"],
              ["Table runner", "Fat quarters & strips", "/c/fat-quarters"],
              ["Bag making", "Sturdy notions & tools", "/c/notions-small-tools"],
              ["English paper piecing", "Patterns & hand tools", "/c/patterns-books"],
              ["Garment sewing", "Threads & sharp scissors", "/c/threads"],
            ].map(([label, sub, href]) => (
              <Link
                key={label}
                to={href}
                className="group rounded-md border border-dashed border-[#C9958A] bg-[#FAF6F1] p-5 transition-colors hover:border-solid hover:border-[#6D2E4B] hover:bg-[#F9F1EB]"
                data-testid={`project-tile-${label.toLowerCase().replace(/ /g, "-")}`}
              >
                <div className="font-display text-[17px] leading-tight text-[#2B2127] group-hover:text-[#6D2E4B]">{label}</div>
                <div className="mt-1 text-[12px] leading-relaxed text-[#6B5158]">{sub}</div>
                <ArrowRight size={14} className="mt-3 text-[#9D5C6C]" />
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-dashed border-[#C9958A] pt-6 text-[14px]" data-testid="resources-strip">
            <span className="text-[#6B5158]">Maker resources:</span>
            <Link to="/guides/precut-sizes" className="text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">Precut size guide</Link>
            <Link to="/guides/quilt-calculators" className="text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">Backing & binding calculator</Link>
            <Link to="/guides/glossary" className="text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">Quilting glossary</Link>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS — COMING SOON */}
      {arrivals.length > 0 && (
        <section className="border-b border-[#E6D4CC] bg-[#F1E3DC] py-16 md:py-24" data-testid="coming-soon-section">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="mb-10 max-w-2xl">
              <div className="text-xs uppercase tracking-[0.3em] text-[#6B5158]">New arrivals</div>
              <h2 className="mt-2 font-display text-4xl text-[#2B2127] md:text-5xl">
                Arriving <span className="font-display-soft italic">soon</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-[#3A2E33]">
                These staples are on their way to our shelves. Every listing is priced and ready to reserve — join a waitlist and we'll email you the moment an item is in stock and ready to ship.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-5">
              {arrivals.slice(0, 5).map((p, i) => <ProductCard key={p.id} product={p} index={i % 4} />)}
            </div>
            <form onSubmit={notifySubscribe} className="mt-12 flex max-w-lg flex-col gap-3 sm:flex-row sm:items-center" data-testid="coming-soon-notify-form">
              <input
                type="email"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="you@example.com — get stock alerts"
                className="flex-1 rounded-full border border-[#E6D4CC] bg-white px-5 py-3 text-sm text-[#2B2127] outline-none placeholder:text-[#6B5158]/60 focus:border-[#9D5C6C]"
                data-testid="coming-soon-notify-input"
              />
              <button type="submit" disabled={nlSending} className="rounded-full bg-[#6D2E4B] px-7 py-3 text-sm font-medium text-white transition-colors hover:bg-[#59253d] disabled:opacity-50" data-testid="coming-soon-notify-submit">
                {nlSending ? "…" : "Get stock alerts"}
              </button>
            </form>
            <p className="mt-3 text-[12px] text-[#6B5158]">One email when stock lands — no spam, unsubscribe anytime.</p>
          </div>
        </section>
      )}

      {/* LIFESTYLE / EDITORIAL */}
      <section className="border-b border-[#E6D4CC] bg-[#FAF6F1] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-20 md:px-10">
          <div className="order-2 md:order-1">
            <div className="aspect-[5/4] overflow-hidden rounded-md bg-white">
              <img src={LIFESTYLE_IMG} alt="A handmade quilt draped in a cozy home" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">From makers, for makers</div>
            <h2 className="font-display text-4xl leading-[1.1] text-[#2B2127] md:text-[56px]">
              Accurate cuts. <br />
              True colors. <br />
              <span className="font-display-soft italic">Honest</span> supplies.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#3A2E33]">
              A quilt holds hundreds of small decisions — and every one of them is easier with tools you trust. We carry a focused catalog of staples we'd put on our own cutting tables, and nothing we wouldn't.
            </p>
            <Link to="/pages/our-story" className="mt-6 inline-flex items-center gap-2 text-sm text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">
              Read our story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* B2B / WHOLESALE STRIP */}
      <section className="relative overflow-hidden border-b border-[#E6D4CC]" data-testid="b2b-strip">
        <div className="absolute inset-0">
          <img src={B2B_IMG} alt="Bolts of quilting fabric on shop shelves" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#3d1a2c]/85" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-6 py-16 md:px-10 md:py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/70">
              <PackageCheck size={14} /> For the trade
            </div>
            <h2 className="mt-3 font-display text-3xl leading-tight text-white md:text-5xl">
              Run a quilt shop, sewing studio, or longarm business?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/85">
              We offer small-batch wholesale for local studios, independent makers, and pattern designers — fair minimums, honest pricing, and a direct line to people who understand the craft. Tell us about your business and we'll reply within 1–2 business days.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/wholesale" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-[#3d1a2c] transition-colors hover:bg-[#F1E3DC]" data-testid="b2b-cta">
                Wholesale inquiries <ArrowRight size={15} />
              </Link>
              <a href={`mailto:${BRAND.wholesaleEmail}`} className="text-sm text-white/85 underline underline-offset-4 hover:text-white">{BRAND.wholesaleEmail}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
